/*
 * src/utils/api.ts
 * Description: Utility functions for making authenticated API requests using the stored JWT token from Redux.
 */

import { store } from '../store'; // Import the Redux store

// Helper function to get the token from the Redux store
export const getToken = (): string | null => {
    const state = store.getState();
    return state.user.token;
};

// Utility function to handle API requests (used by other methods)
const fetchWithToken = async (url: string, options: RequestInit): Promise<any> => {
    const token = getToken();  // Retrieve the token from Redux state
    if (!token) throw new Error('No token found');

    // Add Authorization header if token is available
    options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(`${process.env.GATSBY_BACKEND_URL}${url}`, options);
    const responseData = await response.json();

    if (!response.ok) {
        console.error('API Error', { status: response.status, statusText: response.statusText, response: responseData });
        throw new Error(responseData.error || `API request failed: ${response.status} ${response.statusText}`);
    }

    return responseData;
};

// Function for making GET requests to protected API endpoints
export const fetchProtectedData = async (): Promise<any> => {
    return fetchWithToken('/wp-json/wp/v2/posts', {
        method: 'GET',
    });
};

// Function for making POST requests (handling both JSON and FormData)
export const postData = async (url: string, data: any): Promise<any> => {
    const headers: HeadersInit = {};

    // Only set Content-Type for JSON payloads, skip it for FormData
    if (!(data instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    return fetchWithToken(url, {
        method: 'POST',
        headers,
        body: data instanceof FormData ? data : JSON.stringify(data),
    });
};

// Function for making PUT requests (handles JSON data)
export const putData = async (url: string, data: any): Promise<any> => {
    return fetchWithToken(url, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
};

// New function to handle DELETE requests
export const deleteData = async (url: string): Promise<any> => {
    return fetchWithToken(url, {
        method: 'DELETE',
    });
};


// Function to upload the card bitmap image
export const uploadCardBitmapImage = async (formData: FormData): Promise<any> => {
    const token = getToken();
    if (!token) {
        throw new Error('No token found');
    }

    const response = await fetch(`${process.env.GATSBY_BACKEND_URL}/wp-json/invidoo/v1/upload-card-bitmap-image`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}` // Include the JWT token for authentication
        },
        body: formData,
    });

    if (!response.ok) {
        throw new Error('Failed to upload card bitmap image');
    }
    return await response.json();
};